import React, { useEffect, useState } from "react";
import axios from "axios";
import { BadgePercent, Star, ShieldAlert, MessageCircle, Sparkles } from "lucide-react";
import { AliasManagerCard } from "./AliasManager";
import { toast } from "sonner";
import { API } from "../../lib/store";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";

const PLACES = [
  { key: "home", label: "Home page banner (animated)" },
  { key: "product", label: "Product pages (near price)" },
  { key: "catalog", label: "Shop, collection & wishlist pages" },
  { key: "inquiry", label: "Inquiry form (before submit)" },
  { key: "footer", label: "Footer (every page)" },
];

const EXP_TOGGLES = [
  { key: "ambientSound", label: "Ambient hum (432 Hz)", desc: "The soft background tone in the Regal Vault and inside every world." },
  { key: "voiceNarration", label: "Mr. Regal voice narration", desc: "Page voice lines, world greetings, product stories and closing lines site-wide." },
  { key: "voiceCommands", label: "\u201cHey Regal\u201d voice commands", desc: "Wake-phrase voice search, collection opening and spoken questions (mic pill at bottom of the page)." },
  { key: "introEntrance", label: "Homepage intro entrance", desc: "Mr. Regal's welcome screen for first-time visitors. Off = straight to the vault." },
  { key: "journeyPrompt", label: "\u201cWhat brings you here today?\u201d prompt", desc: "The six-option question that personalizes doors, products and chat." },
  { key: "chatWidget", label: "Ask Mr. Regal chat bubble", desc: "The AI concierge chat button in the corner of every page." },
  { key: "reviewPrompt", label: "Google review prompt", desc: "The review invitation after inquiries and bookings." },
  { key: "occasionReminders", label: "Occasion reminders", desc: "Save-a-special-date card in the vault and the return-visit reminder bar." },
  { key: "goldCursor", label: "Gold cursor (desktop)", desc: "The gold diamond cursor and trail on desktop browsers." },
];
const VOLUMES = [
  { v: 0.05, label: "Whisper" },
  { v: 0.12, label: "Soft" },
  { v: 0.2, label: "Clear" },
];

function ExperienceCard({ auth }) {
  const [exp, setExp] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/experience`, auth)
      .then((r) => setExp(r.data))
      .catch((e) => console.warn("Experience fetch failed:", e?.message));
  }, [auth]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/experience`, exp, auth);
      window.dispatchEvent(new Event("rj-notice-updated"));
      toast.success("Experience controls saved — live for every new visit, no redeploy needed");
    } catch {
      toast.error("Could not save experience controls");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#eee6d6] p-6" data-testid="experience-settings">
      <h3 className="font-heading text-xl text-[#171310] flex items-center gap-2">
        <Sparkles size={18} className="text-[#a8842c]" /> Experience Controls
      </h3>
      <p className="text-sm text-[#8f8571] mt-1">
        Master switches for everything Mr. Regal does on the site — sound, voice, intro, prompts, chat and more.
        Changes apply instantly to visitors on their next page load. Products, cart and checkout are never affected.
      </p>
      {!exp ? (
        <p className="text-sm text-[#8f8571] mt-4">Loading...</p>
      ) : (
        <>
          <div className="grid lg:grid-cols-2 gap-3 mt-5 items-start">
            {EXP_TOGGLES.map((t) => (
              <div key={t.key} className="border border-[#eee6d6] p-4">
                <div className="flex items-center justify-between">
                  <div className="pr-4">
                    <Label className="text-sm text-[#171310] font-medium">{t.label}</Label>
                    <p className="text-xs text-[#8f8571] mt-0.5">{t.desc}</p>
                  </div>
                  <Switch checked={exp[t.key] !== false} onCheckedChange={(v) => setExp({ ...exp, [t.key]: v })} data-testid={`exp-${t.key}-switch`} />
                </div>
                {t.key === "ambientSound" && exp.ambientSound !== false && (
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-[11px] uppercase tracking-wider text-[#7a7160] mr-1">Volume:</span>
                    {VOLUMES.map((o) => (
                      <button
                        key={o.v}
                        onClick={() => setExp({ ...exp, ambientVolume: o.v })}
                        className={`text-[11px] tracking-[0.12em] uppercase px-3 py-1.5 border transition-colors ${Math.abs((exp.ambientVolume ?? 0.12) - o.v) < 0.02 ? "bg-[#171310] text-[#e9d9ac] border-[#171310]" : "border-[#d8cdb5] text-[#5c5240] hover:border-[#a8842c]"}`}
                        data-testid={`exp-volume-${o.label.toLowerCase()}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <Button onClick={save} disabled={saving} className="mt-5 rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] tracking-[0.12em] uppercase text-xs h-11 px-8 transition-colors duration-300" data-testid="exp-save-btn">
            {saving ? "Saving..." : "Save Experience Controls"}
          </Button>
        </>
      )}
    </div>
  );
}

function WhatsAppAlertsCard({ auth }) {
  const [recipients, setRecipients] = useState(null);
  const [keys, setKeys] = useState({ key1: "", key2: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/whatsapp-alerts`, auth)
      .then((r) => setRecipients(r.data.recipients))
      .catch((e) => console.warn("WhatsApp alerts fetch failed:", e?.message));
  }, [auth]);

  const saveKeys = async () => {
    const payload = {};
    if (keys.key1.trim()) payload.key1 = keys.key1.trim();
    if (keys.key2.trim()) payload.key2 = keys.key2.trim();
    if (!Object.keys(payload).length) return toast.error("Paste at least one API key first");
    setSaving(true);
    try {
      await axios.post(`${API}/admin/whatsapp-alerts`, payload, auth);
      const r = await axios.get(`${API}/admin/whatsapp-alerts`, auth);
      setRecipients(r.data.recipients);
      setKeys({ key1: "", key2: "" });
      toast.success("WhatsApp alert key saved");
    } catch {
      toast.error("Could not save the key");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/admin/whatsapp-alerts/test`, {}, auth);
      const ok = r.data.results.filter((x) => x.ok).length;
      if (ok) toast.success(`Test message sent to ${ok} phone${ok > 1 ? "s" : ""} — check WhatsApp now`);
      else toast.error("No test sent — activate a phone and save its API key first");
    } catch {
      toast.error("Test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-white border border-[#eee6d6] p-6" data-testid="wa-alerts-settings">
      <h3 className="font-heading text-xl text-[#171310] flex items-center gap-2">
        <MessageCircle size={18} className="text-[#a8842c]" /> WhatsApp Inquiry Alerts
      </h3>
      <p className="text-sm text-[#8f8571] mt-1">
        Every product inquiry is instantly sent by WhatsApp to your two private phones. The numbers live only in the
        backend — they never appear on the website. One-time activation per phone: save <strong>+34 644 33 66 63</strong> in
        WhatsApp, send it the message <em>"I allow callmebot to send me messages"</em>, then paste the API key it replies with below.
      </p>
      {!recipients ? (
        <p className="text-sm text-[#8f8571] mt-4">Loading...</p>
      ) : (
        <>
          <div className="space-y-3 mt-5">
            {recipients.map((r) => (
              <div key={r.slot} className="border border-[#eee6d6] p-4">
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-[#171310] font-medium">Phone {r.phoneMask}</Label>
                  <span
                    className={`text-[10px] tracking-[0.15em] uppercase px-2 py-1 ${r.active ? "bg-[#3f6b3a] text-white" : "bg-[#faf7f0] text-[#8f8571] border border-[#eee6d6]"}`}
                    data-testid={`wa-status-${r.slot}`}
                  >
                    {r.active ? "Active" : "Awaiting activation"}
                  </span>
                </div>
                <Input
                  type="password"
                  value={keys[`key${r.slot}`]}
                  onChange={(e) => setKeys({ ...keys, [`key${r.slot}`]: e.target.value })}
                  placeholder={r.active ? "Paste a new API key to replace the saved one" : "Paste this phone's API key from CallMeBot"}
                  className="rounded-none border-[#d8cdb5] h-11 mt-3"
                  data-testid={`wa-key-input-${r.slot}`}
                />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-5">
            <Button onClick={saveKeys} disabled={saving} className="rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] tracking-[0.12em] uppercase text-xs h-11 px-8 transition-colors duration-300" data-testid="wa-save-keys-btn">
              {saving ? "Saving..." : "Save Keys"}
            </Button>
            <Button onClick={sendTest} disabled={testing} variant="outline" className="rounded-none border-[#d8cdb5] text-[#171310] tracking-[0.12em] uppercase text-xs h-11 px-6" data-testid="wa-test-btn">
              {testing ? "Sending..." : "Send Test Message"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function SettingsTab({ auth }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reviewLink, setReviewLink] = useState("");
  const [savingLink, setSavingLink] = useState(false);
  const [op, setOp] = useState(null);
  const [savingOp, setSavingOp] = useState(false);
  const [blText, setBlText] = useState({ emails: "", phones: "", addresses: "" });

  useEffect(() => {
    axios.get(`${API}/site-status`)
      .then((r) => {
        setForm(r.data.ccNotice);
        setReviewLink(r.data.googleReviewLink || "");
      })
      .catch((e) => console.warn("Settings fetch failed:", e?.message));
  }, []);

  const token = auth.headers.Authorization;
  useEffect(() => {
    axios.get(`${API}/admin/order-protection`, { headers: { Authorization: token } })
      .then((r) => {
        setOp(r.data);
        setBlText({
          emails: (r.data.blocklist.emails || []).join("\n"),
          phones: (r.data.blocklist.phones || []).join("\n"),
          addresses: (r.data.blocklist.addresses || []).join("\n"),
        });
      })
      .catch((e) => console.warn("Order protection fetch failed:", e?.message));
  }, [token]);

  if (!form) return <p className="text-[#8f8571] text-sm py-10 text-center">Loading settings...</p>;

  const setPercent = (v) => {
    const p = Math.max(0, Number(v) || 0);
    setForm((f) => ({ ...f, percent: p, text: `${p}% EXTRA ON ALL CREDIT CARD PURCHASES` }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/cc-notice`, form, auth);
      window.dispatchEvent(new Event("rj-notice-updated"));
      toast.success("Payment notice updated across the entire site");
    } catch {
      toast.error("Could not save the notice settings");
    } finally {
      setSaving(false);
    }
  };

  const saveReviewLink = async () => {
    setSavingLink(true);
    try {
      await axios.post(`${API}/admin/review-link`, { url: reviewLink }, auth);
      window.dispatchEvent(new Event("rj-notice-updated"));
      toast.success(reviewLink.trim() ? "Google review link saved — review asks are now live on the site" : "Review link cleared — review asks are hidden");
    } catch {
      toast.error("Could not save the review link");
    } finally {
      setSavingLink(false);
    }
  };

  const saveOrderProtection = async () => {
    setSavingOp(true);
    try {
      const payload = {
        enquiryFirst: op.enquiryFirst,
        manualApproval: op.manualApproval,
        blocklist: {
          emails: blText.emails.split("\n").map((s) => s.trim()).filter(Boolean),
          phones: blText.phones.split("\n").map((s) => s.trim()).filter(Boolean),
          addresses: blText.addresses.split("\n").map((s) => s.trim()).filter(Boolean),
        },
      };
      const r = await axios.post(`${API}/admin/order-protection`, payload, auth);
      setOp({ enquiryFirst: r.data.enquiryFirst, manualApproval: r.data.manualApproval, blocklist: r.data.blocklist });
      toast.success("Order protection settings saved");
    } catch {
      toast.error("Could not save order protection");
    } finally {
      setSavingOp(false);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <ExperienceCard auth={auth} />

      <AliasManagerCard auth={auth} />

      <WhatsAppAlertsCard auth={auth} />

      {/* Order protection */}
      <div className="bg-white border border-[#eee6d6] p-6" data-testid="order-protection-settings">
        <h3 className="font-heading text-xl text-[#171310] flex items-center gap-2">
          <ShieldAlert size={18} className="text-[#a8842c]" /> Order Protection
        </h3>
        <p className="text-sm text-[#8f8571] mt-1">
          Anti-fraud controls — currently DORMANT (all off by default): the site behaves exactly as before until you switch a toggle on or add blocklist entries. Once active, incoming inquiries and bookings are screened automatically — matches get a red risk banner, and blocklisted contacts are stored silently without alerting you or tipping off the sender.
        </p>
        {!op ? (
          <p className="text-sm text-[#8f8571] mt-4">Loading...</p>
        ) : (
          <>
            <div className="flex items-center justify-between border border-[#eee6d6] p-4 mt-5">
              <div>
                <Label className="text-sm text-[#171310] font-medium">Enquiry-first mode</Label>
                <p className="text-xs text-[#8f8571] mt-0.5">No instant online payment anywhere — product CTAs stay Enquire / Book Appointment / WhatsApp. No card is ever charged online.</p>
              </div>
              <Switch checked={op.enquiryFirst} onCheckedChange={(v) => setOp({ ...op, enquiryFirst: v })} data-testid="op-enquiry-first-switch" />
            </div>
            <div className="flex items-center justify-between border border-[#eee6d6] p-4 mt-3">
              <div>
                <Label className="text-sm text-[#171310] font-medium">Manual approval mode</Label>
                <p className="text-xs text-[#8f8571] mt-0.5">If online ordering is ever enabled: every order lands PENDING — nothing charges or ships until you approve it, with automatic risk flags (address mismatch, large first order, repeat declines, same card / different names).</p>
              </div>
              <Switch checked={op.manualApproval} onCheckedChange={(v) => setOp({ ...op, manualApproval: v })} data-testid="op-manual-approval-switch" />
            </div>

            <div className="mt-5">
              <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Blocklist — one entry per line</Label>
              <div className="grid sm:grid-cols-3 gap-3 mt-3">
                {[["emails", "Emails"], ["phones", "Phone numbers"], ["addresses", "Addresses"]].map(([key, label]) => (
                  <div key={key}>
                    <p className="text-[11px] text-[#8f8571] mb-1">{label}</p>
                    <textarea
                      value={blText[key]}
                      onChange={(e) => setBlText({ ...blText, [key]: e.target.value })}
                      rows={4}
                      placeholder={key === "emails" ? "scammer@example.com" : key === "phones" ? "312-555-0000" : "123 Fake St, City"}
                      className="w-full border border-[#d8cdb5] bg-white px-2.5 py-2 text-xs outline-none focus:border-[#a8842c] transition-colors"
                      data-testid={`op-blocklist-${key}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <Button onClick={saveOrderProtection} disabled={savingOp} className="mt-5 rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] tracking-[0.12em] uppercase text-xs h-11 px-8 transition-colors duration-300" data-testid="op-save-btn">
              {savingOp ? "Saving..." : "Save Order Protection"}
            </Button>
          </>
        )}
      </div>

      {/* Google review link */}
      <div className="bg-white border border-[#eee6d6] p-6" data-testid="review-link-settings">
        <h3 className="font-heading text-xl text-[#171310] flex items-center gap-2">
          <Star size={18} className="text-[#a8842c]" /> Google Review Link
        </h3>
        <p className="text-sm text-[#8f8571] mt-1">
          Paste your Google review link (g.page/r/.../review). Wherever the site invites customers to review, it always
          shows this link alongside a private-feedback option — never filtered by rating. Leave empty to hide review asks.
        </p>
        <div className="flex flex-wrap gap-3 mt-4">
          <Input value={reviewLink} onChange={(e) => setReviewLink(e.target.value)} placeholder="https://g.page/r/XXXXXXXX/review" className="rounded-none border-[#d8cdb5] h-11 flex-1 min-w-[240px]" data-testid="review-link-input" />
          <Button onClick={saveReviewLink} disabled={savingLink} className="rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] tracking-[0.12em] uppercase text-xs h-11 px-6 transition-colors duration-300" data-testid="review-link-save-btn">
            {savingLink ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {/* Credit card notice */}
      <div className="bg-white border border-[#eee6d6] p-6" data-testid="cc-notice-settings">
        <h3 className="font-heading text-xl text-[#171310] flex items-center gap-2">
          <BadgePercent size={18} className="text-[#a8842c]" /> Credit Card Purchase Notice
        </h3>
        <p className="text-sm text-[#8f8571] mt-1">
          One global setting controls the notice everywhere on the site. Change the percentage or wording once and every page updates instantly — no redeploy needed.
        </p>

        <div className="flex items-center justify-between border border-[#eee6d6] p-4 mt-6">
          <div>
            <Label className="text-sm text-[#171310] font-medium">Show notice site-wide</Label>
            <p className="text-xs text-[#8f8571] mt-0.5">Turning this off removes the notice everywhere.</p>
          </div>
          <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="cc-notice-enabled-switch" />
        </div>

        <div className="grid sm:grid-cols-[120px_1fr] gap-4 mt-5">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Percentage</Label>
            <Input type="number" min="0" step="0.5" value={form.percent} onChange={(e) => setPercent(e.target.value)} className="rounded-none border-[#d8cdb5] h-11" data-testid="cc-notice-percent-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Notice wording</Label>
            <Input value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="rounded-none border-[#d8cdb5] h-11 font-bold uppercase" data-testid="cc-notice-text-input" />
          </div>
        </div>

        <div className="flex items-center justify-between border border-[#eee6d6] p-4 mt-5">
          <div>
            <Label className="text-sm text-[#171310] font-medium">Homepage animation</Label>
            <p className="text-xs text-[#8f8571] mt-0.5">Soft gold glow pulse on the home banner (auto-disabled for reduced-motion users).</p>
          </div>
          <Switch checked={form.animate !== false} onCheckedChange={(v) => setForm({ ...form, animate: v })} data-testid="cc-notice-animate-switch" />
        </div>

        <div className="mt-5">
          <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Where the notice appears</Label>
          <div className="space-y-2.5 mt-3">
            {PLACES.map((p) => (
              <label key={p.key} className="flex items-center gap-3 text-sm text-[#3a352c] cursor-pointer">
                <Checkbox
                  checked={form.show?.[p.key] !== false}
                  onCheckedChange={(v) => setForm({ ...form, show: { ...form.show, [p.key]: Boolean(v) } })}
                  data-testid={`cc-notice-place-${p.key}`}
                />
                {p.label}
              </label>
            ))}
          </div>
        </div>

        <div className="border border-[#eee6d6] bg-[#faf7f0] p-4 mt-6 text-center">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#9c9482] mb-2">Preview</p>
          <p className="font-bold text-[#171310] text-sm sm:text-base tracking-[0.12em] uppercase">{form.text}</p>
        </div>

        <Button onClick={save} disabled={saving} className="mt-6 rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] tracking-[0.12em] uppercase text-xs h-11 px-8 transition-colors duration-300" data-testid="cc-notice-save-btn">
          {saving ? "Saving..." : "Save Notice Settings"}
        </Button>
      </div>
    </div>
  );
}
